import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { 
  setLoading, 
  setData, 
  setRawResponse,
  setError, 
  setActiveTab, 
  setTabData, 
  setTabLoading, 
  setTabError, 
  setDimensionNames,
  clearData 
} from 'features/underlying-data-slice';
import { useAiChatApiService } from 'api/providers/AppServiceProvider';

export const useUnderlyingData = () => {
  const dispatch = useAppDispatch();
  const aiChatApiService = useAiChatApiService();
  
  const { 
    data, 
    rawResponse,
    loading, 
    error, 
    activeTab, 
    tabs,
    dimensionNames 
  } = useAppSelector((state) => state.underlyingData);

  const loadUnderlyingData = useCallback(async (conversationId: string, messageId: string) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearData());
      
      // Load initial data (first dimension only)
      const response = await aiChatApiService.getUnderlyingData(conversationId, messageId);
      
      // Handle the new API response structure
      if (response.status === 'success' && response.results && response.results.length > 0) {
        // Store the full response for time_insights access
        dispatch(setRawResponse(response));
        
        // Set dimension names and create tabs dynamically
        dispatch(setDimensionNames(response.dimension_names || []));
        
        // Set time_insights as the default active tab
        dispatch(setActiveTab('time_insights'));
        
        // Set main data from the first result (this will be used for time_insights display)
        const firstResult = response.results[0];
        dispatch(setData(firstResult.data || []));
        
        // Create empty tabs for ALL dimensions (they will be loaded on demand)
        response.dimension_names.forEach((dimensionName: string, index: number) => {
          const tabId = `tab-${index + 1}`;
          dispatch(setTabData({ 
            tabId: tabId, 
            data: [] 
          }));
        });
        
        
      } else {
        // Handle error case
        dispatch(setError(response.error || 'No data available'));
      }
      
      // Set loading to false after successful data processing
      dispatch(setLoading(false));
      
    } catch (error: any) {
      dispatch(setError(error.message || 'Failed to load underlying data'));
      dispatch(setLoading(false));
    }
  }, [dispatch, aiChatApiService]);

  const loadTabData = useCallback(async (tabId: string, conversationId: string, messageId: string, dimensionNames?: string[]) => {
    try {
      dispatch(setTabLoading({ tabId, loading: true }));
      
      // Get the dimension name for this tab
      const tabIndex = parseInt(tabId.replace('tab-', '')) - 1;
      const currentDimensionNames = dimensionNames || [];
      const dimensionName = currentDimensionNames[tabIndex];
      
      if (!dimensionName) {
        dispatch(setTabError({ tabId, error: 'Dimension name not found' }));
        return;
      }
      
      // Load specific tab data based on dimension name
      const response = await aiChatApiService.getUnderlyingData(conversationId, messageId, dimensionName);
      
      let tabData = [];
      if (response.status === 'success' && response.results && response.results.length > 0) {
        // Find the result for this specific dimension
        const result = response.results.find((r: any) => r.dimension_name === dimensionName);
        
        if (result) {
          tabData = result.data || [];
        }
      }
      
      dispatch(setTabData({ tabId, data: tabData }));
      dispatch(setTabLoading({ tabId, loading: false }));
      
    } catch (error: any) {
      dispatch(setTabError({ tabId, error: error.message || 'Failed to load tab data' }));
      dispatch(setTabLoading({ tabId, loading: false }));
    }
  }, [dispatch, aiChatApiService]);

  const switchTab = useCallback((tabId: string) => {
    dispatch(setActiveTab(tabId));
  }, [dispatch]);

  const clearUnderlyingData = useCallback(() => {
    dispatch(clearData());
  }, [dispatch]);

  return {
    // State
    data,
    loading,
    error,
    activeTab,
    tabs,
    dimensionNames,
    
    // Actions
    loadUnderlyingData,
    loadTabData,
    switchTab,
    clearUnderlyingData,
  };
};
